import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Plane, Search, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { getFlights, addFlight, updateFlight, deleteFlight } from '../firebase/firestore';

const EMPTY = {
  airline: '', airlineCode: '', flightNumber: '', from: '', to: '',
  departureTime: '', arrivalTime: '', duration: '', stops: 0,
  price: '', cabinClass: 'Economy', seatsLeft: 0, active: true,
};

export default function Flights() {
  const [flights, setFlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null); // null | 'add' | flight object
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    getFlights().then(d => { setFlights(d); setLoading(false); });
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => { setForm(EMPTY); setModal('add'); };
  const openEdit = f => { setForm({ ...f }); setModal(f); };
  const close = () => setModal(null);

  const save = async () => {
    if (!form.airline || !form.flightNumber || !form.from || !form.to) {
      toast.error('Fill in required fields'); return;
    }
    setSaving(true);
    try {
      if (modal === 'add') {
        await addFlight({ ...form, price: Number(form.price), stops: Number(form.stops), seatsLeft: Number(form.seatsLeft) });
        toast.success('Flight added');
      } else {
        await updateFlight(modal.id, { ...form, price: Number(form.price), stops: Number(form.stops), seatsLeft: Number(form.seatsLeft) });
        toast.success('Flight updated');
      }
      close(); load();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const remove = async f => {
    if (!window.confirm(`Delete flight ${f.flightNumber}?`)) return;
    await deleteFlight(f.id);
    toast.success('Deleted'); load();
  };

  const filtered = flights.filter(f =>
    `${f.airline} ${f.flightNumber} ${f.from} ${f.to}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-white font-bold text-xl">Flights</h2>
          <p className="text-[#546E7A] text-sm">{flights.length} flights in system</p>
        </div>
        <button onClick={openAdd}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#2D5BE3] hover:bg-[#1A3C8F]
            text-white rounded-xl text-sm font-semibold transition-colors">
          <Plus size={16} /> Add Flight
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#546E7A]" />
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search flights..."
          className="w-full pl-9 pr-4 py-2.5 bg-[#1A2235] border border-[#1E2D45] rounded-xl
            text-white text-sm placeholder-[#546E7A] focus:outline-none focus:border-[#2D5BE3]" />
      </div>

      {/* Table */}
      <div className="bg-[#1A2235] border border-[#1E2D45] rounded-2xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[#1E2D45]">
              {['Airline', 'Flight', 'Route', 'Time', 'Price', 'Seats', 'Status', ''].map(h => (
                <th key={h} className="text-left text-xs text-[#546E7A] font-semibold px-4 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center py-12 text-[#546E7A]">Loading...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-12 text-[#546E7A]">No flights found</td></tr>
            ) : filtered.map(f => (
              <tr key={f.id} className="border-b border-[#1E2D45] hover:bg-white/2 transition-colors">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-[#2D5BE3]/20 flex items-center justify-center">
                      <span className="text-[#2D5BE3] text-xs font-bold">{f.airlineCode}</span>
                    </div>
                    <span className="text-white text-sm font-medium">{f.airline}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-[#B0BEC5] text-sm">{f.flightNumber}</td>
                <td className="px-4 py-3">
                  <span className="text-white text-sm font-semibold">{f.from}</span>
                  <span className="text-[#546E7A] mx-1">→</span>
                  <span className="text-white text-sm font-semibold">{f.to}</span>
                </td>
                <td className="px-4 py-3 text-[#B0BEC5] text-sm">{f.departureTime} – {f.arrivalTime}</td>
                <td className="px-4 py-3 text-[#D4AF37] font-semibold text-sm">${f.price}</td>
                <td className="px-4 py-3 text-[#B0BEC5] text-sm">{f.seatsLeft}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-1 rounded-full font-semibold
                    ${f.active ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                    {f.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button onClick={() => openEdit(f)}
                      className="w-8 h-8 rounded-lg bg-[#2D5BE3]/10 hover:bg-[#2D5BE3]/20
                        flex items-center justify-center text-[#2D5BE3] transition-colors">
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => remove(f)}
                      className="w-8 h-8 rounded-lg bg-red-500/10 hover:bg-red-500/20
                        flex items-center justify-center text-red-400 transition-colors">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {modal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-[#111827] border border-[#1E2D45] rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-[#1E2D45]">
              <h3 className="text-white font-bold">{modal === 'add' ? 'Add Flight' : 'Edit Flight'}</h3>
              <button onClick={close} className="text-[#546E7A] hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                {[
                  ['Airline *', 'airline', 'text'],
                  ['Airline Code', 'airlineCode', 'text'],
                  ['Flight Number *', 'flightNumber', 'text'],
                  ['From (IATA) *', 'from', 'text'],
                  ['To (IATA) *', 'to', 'text'],
                  ['Departure Time', 'departureTime', 'text'],
                  ['Arrival Time', 'arrivalTime', 'text'],
                  ['Duration', 'duration', 'text'],
                  ['Price ($) *', 'price', 'number'],
                  ['Seats Left', 'seatsLeft', 'number'],
                  ['Stops', 'stops', 'number'],
                ].map(([label, key, type]) => (
                  <div key={key}>
                    <label className="block text-xs text-[#B0BEC5] mb-1">{label}</label>
                    <input type={type} value={form[key]} onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                      className="w-full px-3 py-2 bg-[#1A2235] border border-[#1E2D45] rounded-xl
                        text-white text-sm focus:outline-none focus:border-[#2D5BE3]" />
                  </div>
                ))}
                <div>
                  <label className="block text-xs text-[#B0BEC5] mb-1">Cabin Class</label>
                  <select value={form.cabinClass} onChange={e => setForm(p => ({ ...p, cabinClass: e.target.value }))}
                    className="w-full px-3 py-2 bg-[#1A2235] border border-[#1E2D45] rounded-xl
                      text-white text-sm focus:outline-none focus:border-[#2D5BE3]">
                    {['Economy', 'Premium Economy', 'Business', 'First Class'].map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-3 pt-4">
                  <label className="text-sm text-[#B0BEC5]">Active</label>
                  <button onClick={() => setForm(p => ({ ...p, active: !p.active }))}
                    className={`w-10 h-6 rounded-full transition-colors ${form.active ? 'bg-[#2D5BE3]' : 'bg-[#1E2D45]'}`}>
                    <div className={`w-4 h-4 bg-white rounded-full mx-1 transition-transform
                      ${form.active ? 'translate-x-4' : 'translate-x-0'}`} />
                  </button>
                </div>
              </div>
            </div>
            <div className="flex gap-3 p-5 border-t border-[#1E2D45]">
              <button onClick={close} className="flex-1 py-2.5 bg-[#1A2235] border border-[#1E2D45]
                text-[#B0BEC5] rounded-xl text-sm hover:text-white transition-colors">Cancel</button>
              <button onClick={save} disabled={saving}
                className="flex-1 py-2.5 bg-[#2D5BE3] hover:bg-[#1A3C8F] text-white rounded-xl
                  text-sm font-semibold transition-colors disabled:opacity-50">
                {saving ? 'Saving...' : 'Save Flight'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
